import chroma from "chroma-js";
import classNames from "classnames";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import * as React from "react";
import tw from "twin.macro";
import { css, styled } from "webapp/styled-components";
import { UIBackgroundStyle, UIButtonAppearance } from "wire/types";
import { useSectionStyle } from "../wire-container/wire-container";

export interface WireButtonProps
    extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    readonly appearance: UIButtonAppearance;
    readonly href?: string;
    readonly target?: string;
    readonly size?: "sm" | "md" | "lg";
}

const WireButtonStyle = styled.button`
    &.sm {
        padding: 8px 18px;
    }

    &.md {
        padding: 10px 20px;
        ${tw`text-base`}
    }

    &.lg {
        padding: 14px 28px;
        ${tw`text-base`}
    }
`;

export const WireButton = React.forwardRef<HTMLButtonElement, WireButtonProps>((p, ref) => {
    const { appearance, children, disabled, size = "sm", href, target, ...rest } = p;

    const theme = useWireAppTheme();

    let style: React.CSSProperties | undefined;
    if (appearance === UIButtonAppearance.Transparent) {
        style = {
            backgroundColor: chroma(theme.accent).alpha(0.2).css(),
        };
    }

    const sectionStyle = useSectionStyle();

    const isAccent = sectionStyle === UIBackgroundStyle.Accent;
    const f = appearance === UIButtonAppearance.Filled;
    const b = appearance === UIButtonAppearance.Bordered;
    const simple = appearance === UIButtonAppearance.Simple;
    const transparent = appearance === UIButtonAppearance.Transparent;
    const mobilePrimary = appearance === UIButtonAppearance.MobilePrimary;
    const mobileSecondary = appearance === UIButtonAppearance.MobileSecondary;
    const floating = appearance === UIButtonAppearance.Floating;

    const filledAccent = f && isAccent;
    const filled = f && !isAccent;
    const borderedAccent = b && isAccent;
    const bordered = b && !isAccent;

    const button = (
        <WireButtonStyle
            {...(rest as any)}
            as={href !== undefined ? "a" : undefined}
            disabled={disabled}
            href={href}
            target={target}
            rel={"noopener noreferrer"}
            ref={ref}
            className={classNames(rest.className, size, {
                disabled,
                filledAccent,
                filled,
                borderedAccent,
                bordered,
                simple,
                transparent,
                mobilePrimary,
                mobileSecondary,
                floating,
            })}
            style={{ ...rest.style, ...style }}
            tw="relative block text-center disabled:(opacity-40 pointer-events-none) after:(content inset-0 absolute
                pointer-events-none rounded-lg transition-shadow) text-sm font-semibold leading-5 transition-all
                rounded-lg text-text-base min-w-0"
            css={css`
                &.filledAccent {
                    ${tw`text-accent bg-white shadow-md-dark hover:(shadow-lg-xdark background[rgba(255, 255, 255, .85)])
                      focus-visible:(background[rgba(255, 255, 255, .85)]) focus-visible:after:(ring-white ring-2)`}
                }
                &.filled {
                    ${tw`color[white] bg-accent filter shadow-md-soft hover:(brightness-95 shadow-lg-soft)
                      focus-visible:after:(ring-2 ring-accent -inset-px border-radius[9px])`}
                }
                &.borderedAccent {
                    ${tw`color[white] background-color[rgba(255, 255, 255, .25)] hover:(background-color[rgba(255, 255,
                      255, .17)]) focus-visible:after:(ring-white ring-2)`}
                }
                &.bordered {
                    ${tw`text-text-base shadow-md-dark hover:(text-text-dark shadow-lg-xdark bg-n50) bg-white
                      focus-visible:after:(ring-2 ring-accent)`}
                }
                &.simple {
                    ${tw`text-text-contextual-accent`}
                }
                &.transparent {
                    ${tw`text-text-contextual-accent`}
                }
                &.floating {
                    ${tw`focus-visible:after:(ring-2 ring-accent)`}
                }
                &.disabled {
                    ${tw`pointer-events-none opacity-40`}
                }
                &.mobilePrimary {
                    ${tw`text-accent background-color[rgba(255, 255, 255, .25)]`}
                }
                &.mobileSecondary {
                    ${tw`text-text-base background-color[rgba(255, 255, 255, .25)]`}
                }
            `}>
            <div tw="truncate">{children}</div>
        </WireButtonStyle>
    );
    return button;
});
